import './styles.css';
import interact from 'interactjs';
import { DEFAULT_RELEASE_PROFILE, getReleaseDurationMs } from '../../core/drag';
import { DEFAULT_EDGE_BASE, readNumberSetting } from '../../core/motion';

type SheetMotionConfig = {
    snapOpenMs: number;
    snapMoveMs: number;
    edgeMaxPx: number;
    edgeDecay: number;
};

const DEFAULT_SHEET_MOTION: SheetMotionConfig = {
    snapOpenMs: 400,
    snapMoveMs: 300,
    edgeMaxPx: DEFAULT_EDGE_BASE.maxPx,
    edgeDecay: DEFAULT_EDGE_BASE.decay,
};

type SheetState = {
    root: HTMLElement;
    panel: HTMLElement;
    overlay: HTMLElement;
    content: HTMLElement | null;
    closeButtons: HTMLElement[];
    openButtons: HTMLElement[];
    dots: HTMLElement[];
    snapRatios: number[];
    currentSnapIndex: number;
    open: boolean;
    dragging: boolean;
    dragStartY: number;
    dragStartTranslate: number;
    translateY: number;
    drawerHeight: number;
    velocity: number;
    dragMoved: boolean;
    suppressClickUntil: number;
    closeTimer: number | null;
    snapTimer: number | null;
    prevHtmlOverflow: string;
    prevBodyOverflow: string;
    config: SheetMotionConfig;
    frameRequested: boolean;
    lastLayoutHeight: number;
    lastOverlayOpacity: number;
    lastActiveDot: number;
    dragActivateOffsetY: number;
    lastDrawTranslateY: number;
    dragRawDeltaY: number;
    dragStartMs: number;
    lastMoveMs: number;
};

const SHEET_SELECTOR = '.sheet:not(.calendar-sheet)';
const SHEET_OPEN_SELECTOR = '.sheet-open';
const SHEET_CLOSE_SELECTOR = '.sheet-close';
const SHEET_PANEL_SELECTOR = '.sheet-panel';
const SHEET_OVERLAY_SELECTOR = '.sheet-backdrop';
const SHEET_CONTENT_SELECTOR = '.sheet-content';
const SHEET_DOT_SELECTOR = '.sheet-dot, .snap-dot';


let bound = false;
const states = new WeakMap<HTMLElement, SheetState>();

export function initSheetComponent(): void {
    if (bound) return;
    bound = true;
    document.addEventListener('ui:update', () => mount(document));
}

function mount(scope: ParentNode): void {
    const roots = Array.from(scope.querySelectorAll<HTMLElement>(SHEET_SELECTOR));
    roots.forEach((root) => {
        if (states.has(root)) return;
        const state = createState(root);
        if (!state) return;
        states.set(root, state);
        bind(state);
        applyA11yState(state, false);
        state.root.classList.remove('sheet-open');
        state.panel.style.transform = `translate3d(0, ${state.drawerHeight}px, 0)`;
        state.panel.style.pointerEvents = 'none';
        state.overlay.style.pointerEvents = 'none';
        updateDots(state);
    });
}

function createState(root: HTMLElement): SheetState | null {
    const panel = root.querySelector<HTMLElement>(SHEET_PANEL_SELECTOR);
    const overlay = root.querySelector<HTMLElement>(SHEET_OVERLAY_SELECTOR);
    if (!panel || !overlay) return null;

    const content = root.querySelector<HTMLElement>(SHEET_CONTENT_SELECTOR);
    const closeButtons = Array.from(root.querySelectorAll<HTMLElement>(SHEET_CLOSE_SELECTOR));
    const dots = Array.from(root.querySelectorAll<HTMLElement>(SHEET_DOT_SELECTOR));
    const target = root.id || '';
    const openButtons = target
        ? Array.from(document.querySelectorAll<HTMLElement>(`${SHEET_OPEN_SELECTOR}[data-sheet-target="${target}"]`))
        : [];
    const snapRatios = parseSnaps(root.dataset.sheetSnaps);
    const drawerHeight = window.innerHeight * snapRatios[0];
    const config = readSheetMotionConfig(root);

    return {
        root,
        panel,
        overlay,
        content,
        closeButtons,
        openButtons,
        dots,
        snapRatios,
        currentSnapIndex: Math.min(1, snapRatios.length - 2),
        open: false,
        dragging: false,
        dragStartY: 0,
        dragStartTranslate: 0,
        translateY: drawerHeight,
        drawerHeight,
        velocity: 0,
        dragMoved: false,
        suppressClickUntil: 0,
        closeTimer: null,
        snapTimer: null,
        prevHtmlOverflow: '',
        prevBodyOverflow: '',
        config,
        frameRequested: false,
        lastLayoutHeight: -1,
        lastOverlayOpacity: -1,
        lastActiveDot: -1,
        dragActivateOffsetY: 0,
        lastDrawTranslateY: Number.NaN,
        dragRawDeltaY: 0,
        dragStartMs: 0,
        lastMoveMs: 0,
    };
}

function bind(state: SheetState): void {
    state.openButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            const raw = button.dataset.sheetOpenSnap;
            const ratio = raw ? Number.parseFloat(raw) : Number.NaN;
            openSheet(state, Number.isFinite(ratio) ? ratio : state.snapRatios[1] || state.snapRatios[0]);
        });
    });

    state.closeButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
            if (performance.now() < state.suppressClickUntil) {
                event.preventDefault();
                return;
            }
            event.preventDefault();
            closeSheet(state);
        });
    });

    state.overlay.addEventListener('click', () => {
        if (performance.now() < state.suppressClickUntil) return;
        if (state.open) closeSheet(state);
    });

    state.panel.addEventListener(
        'click',
        (event) => {
            if (performance.now() < state.suppressClickUntil) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        true
    );

    const interactable = interact(state.panel).draggable({
        startAxis: 'y',
        lockAxis: 'y',
        listeners: {
            start(event) {
                onDragStart(state, event);
            },
            move(event) {
                onDragMove(state, event);
            },
            end() {
                onDragEnd(state);
            },
        },
    });
    state.panel.addEventListener('remove', () => interactable.unset(), { once: true });

    document.addEventListener('ui:resize', () => {
        if (!state.root.isConnected || !state.open) return;
        state.drawerHeight = window.innerHeight * state.snapRatios[0];
        queueFrame(state);
    });
}

function onDragStart(state: SheetState, event: Interact.DragEvent): void {
    if (!state.open) return;
    const target = event.target;
    if (target instanceof Element) {
        const noDragEl = target.closest('.no-drag');
        if (noDragEl) return;
    }

    state.dragging = true;
    state.dragStartY = 0;
    state.dragStartTranslate = state.translateY;
    state.velocity = 0;
    state.dragMoved = false;
    state.dragActivateOffsetY = 0;
    state.lastDrawTranslateY = Number.NaN;
    state.dragRawDeltaY = 0;
    state.dragStartMs = performance.now();
    state.lastMoveMs = state.dragStartMs;
    state.panel.style.transition = 'none';
    state.overlay.style.transition = 'none';
    setSnapping(state, false);
}

function onDragMove(state: SheetState, event: Interact.DragEvent): void {
    if (!state.dragging) return;
    event.preventDefault();

    state.dragRawDeltaY += event.dy;
    const dy = state.dragRawDeltaY;
    if (!state.dragMoved) {
        state.dragActivateOffsetY = dy;
    }
    state.dragMoved = true;

    // 瞬間速度（直前フレームとの差分）で計算 → フリック検出が正確になる
    const now = performance.now();
    const dt = Math.max(1, now - state.lastMoveMs);
    state.velocity = event.dy / dt;
    state.lastMoveMs = now;

    const activeDy = dy - state.dragActivateOffsetY;
    const next = state.dragStartTranslate + activeDy;
    state.translateY = edgeResist(state, next);
    updateSnapFromVisible(state);
    queueFrame(state);
}

function onDragEnd(state: SheetState): void {
    if (!state.dragging) return;
    state.dragging = false;
    if (state.dragMoved) {
        state.suppressClickUntil = performance.now() + 320;
    }
    state.panel.style.removeProperty('transition');
    state.overlay.style.removeProperty('transition');
    const releaseDurationMs = getReleaseDurationMs(Math.abs(state.velocity));
    const returnDurationMs = DEFAULT_RELEASE_PROFILE.normal;

    if (state.velocity > 0.6) {
        closeSheet(state, releaseDurationMs);
        return;
    }
    if (state.velocity < -0.6) {
        snapTo(state, 0, returnDurationMs);
        return;
    }

    const visible = state.drawerHeight - state.translateY;
    const distances = state.snapRatios.map((ratio) => Math.abs(visible - window.innerHeight * ratio));
    const nearest = distances.indexOf(Math.min(...distances));
    snapTo(state, nearest, returnDurationMs);
}

function openSheet(state: SheetState, openRatio: number): void {
    if (state.closeTimer !== null) {
        window.clearTimeout(state.closeTimer);
        state.closeTimer = null;
    }

    state.drawerHeight = window.innerHeight * state.snapRatios[0];
    const index = closestSnapIndex(state.snapRatios, openRatio);
    state.currentSnapIndex = index;
    state.translateY = state.drawerHeight;
    state.open = true;
    state.prevHtmlOverflow = document.documentElement.style.overflow;
    state.prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    state.root.classList.add('sheet-open');
    applyA11yState(state, true);
    state.panel.style.pointerEvents = 'auto';
    state.overlay.style.pointerEvents = 'auto';
    setSnapping(state, false);
    applyFrame(state);

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            setSnapping(state, true);
            setTransitionDuration(state, state.config.snapOpenMs);
            state.translateY = offsetFromSnap(state, index);
            applyFrame(state);
            state.snapTimer = window.setTimeout(() => setSnapping(state, false), state.config.snapOpenMs);
        });
    });
}

function closeSheet(state: SheetState, durationMs = state.config.snapMoveMs): void {
    if (!state.open) return;
    setTransitionDuration(state, durationMs);
    setSnapping(state, true);
    state.translateY = state.drawerHeight;
    applyFrame(state);

    if (state.closeTimer !== null) window.clearTimeout(state.closeTimer);
    state.closeTimer = window.setTimeout(() => {
        state.open = false;
        setSnapping(state, false);
        state.translateY = 0;
        document.documentElement.style.overflow = state.prevHtmlOverflow;
        document.body.style.overflow = state.prevBodyOverflow;
        state.root.classList.remove('sheet-open');
        applyA11yState(state, false);
        state.panel.style.pointerEvents = 'none';
        state.overlay.style.pointerEvents = 'none';
        state.closeTimer = null;
    }, durationMs);
}

function snapTo(state: SheetState, index: number, durationMs = state.config.snapMoveMs): void {
    if (index >= state.snapRatios.length - 1) {
        closeSheet(state, durationMs);
        return;
    }

    state.currentSnapIndex = index;
    setTransitionDuration(state, durationMs);
    setSnapping(state, true);
    state.translateY = offsetFromSnap(state, index);
    applyFrame(state);
    if (state.snapTimer !== null) window.clearTimeout(state.snapTimer);
    state.snapTimer = window.setTimeout(() => {
        setSnapping(state, false);
        state.snapTimer = null;
    }, durationMs);
}

function applyFrame(state: SheetState): void {
    state.frameRequested = false;
    if (!Number.isFinite(state.lastDrawTranslateY) || Math.abs(state.lastDrawTranslateY - state.translateY) > 0.01) {
        state.panel.style.transform = `translate3d(0, ${state.translateY}px, 0)`;
        state.lastDrawTranslateY = state.translateY;
    }
    if (state.lastLayoutHeight !== state.drawerHeight) {
        state.lastLayoutHeight = state.drawerHeight;
        state.panel.style.height = `${state.drawerHeight}px`;
        if (state.content) state.content.style.maxHeight = `${Math.max(0, state.drawerHeight - 48)}px`;
    }

    const lowestOpenSnapRatio = state.snapRatios[state.snapRatios.length - 2] ?? state.snapRatios[0];
    const refVisible = window.innerHeight * lowestOpenSnapRatio;
    const progress = (state.drawerHeight - state.translateY) / Math.max(1, refVisible);
    const nextOpacity = Math.max(0, Math.min(1, progress));
    if (Math.abs(nextOpacity - state.lastOverlayOpacity) > 0.001) {
        state.lastOverlayOpacity = nextOpacity;
        state.overlay.style.opacity = String(nextOpacity);
    }
    updateDots(state);
}

function updateDots(state: SheetState): void {
    if (state.dots.length === 0) return;
    const active = Math.min(state.currentSnapIndex, state.dots.length - 1);
    if (active === state.lastActiveDot) return;
    state.lastActiveDot = active;
    state.dots.forEach((dot, idx) => dot.classList.toggle('active', idx === active));
}

function queueFrame(state: SheetState): void {
    if (state.frameRequested) return;
    state.frameRequested = true;
    requestAnimationFrame(() => applyFrame(state));
}

function setSnapping(state: SheetState, value: boolean): void {
    state.panel.classList.toggle('is-snapping', value);
    state.overlay.classList.toggle('is-snapping', value);
}

function setTransitionDuration(state: SheetState, durationMs: number): void {
    state.panel.style.transitionDuration = `${durationMs}ms`;
    state.overlay.style.transitionDuration = `${durationMs}ms`;
}

function updateSnapFromVisible(state: SheetState): void {
    const visible = state.drawerHeight - state.translateY;
    const top = window.innerHeight * state.snapRatios[0];
    const mid = window.innerHeight * (state.snapRatios[1] || state.snapRatios[0]);

    if (visible >= (top + mid) / 2) state.currentSnapIndex = 0;
    else if (visible >= mid / 2) state.currentSnapIndex = Math.min(1, state.snapRatios.length - 1);
    else state.currentSnapIndex = state.snapRatios.length - 1;
}

function offsetFromSnap(state: SheetState, index: number): number {
    return state.drawerHeight - window.innerHeight * state.snapRatios[index];
}

function closestSnapIndex(snaps: number[], ratio: number): number {
    const distances = snaps.map((v) => Math.abs(v - ratio));
    return distances.indexOf(Math.min(...distances));
}

function edgeResist(state: SheetState, value: number): number {
    const edgeMaxPx = state.config.edgeMaxPx;
    const edgeDecay = state.config.edgeDecay;
    if (value >= 0) return value;
    const v = Math.abs(value);
    const resisted = edgeMaxPx * (1 - Math.exp(-v / edgeDecay));
    return -Math.min(edgeMaxPx, resisted);
}

function parseSnaps(raw?: string): number[] {
    if (!raw) return [0.92, 0.52, 0];
    const parsed = raw
        .split(',')
        .map((v) => Number.parseFloat(v.trim()))
        .filter((v) => Number.isFinite(v) && v >= 0 && v <= 1)
        .sort((a, b) => b - a);
    if (parsed.length === 0) return [0.92, 0.52, 0];
    if (parsed[parsed.length - 1] !== 0) parsed.push(0);
    return parsed;
}

function applyA11yState(state: SheetState, isOpen: boolean): void {
    const label = state.root.dataset.sheetLabel?.trim() || 'Sheet';
    state.root.setAttribute('role', 'dialog');
    state.root.setAttribute('aria-modal', 'true');
    state.root.setAttribute('aria-label', label);
    state.root.setAttribute('tabindex', '-1');
    state.root.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    state.panel.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
}

function readSheetMotionConfig(root: HTMLElement): SheetMotionConfig {
    return {
        snapOpenMs: readNumberSetting(root.dataset.sheetSnapOpenMs, DEFAULT_SHEET_MOTION.snapOpenMs, 0),
        snapMoveMs: readNumberSetting(root.dataset.sheetSnapMoveMs, DEFAULT_SHEET_MOTION.snapMoveMs, 0),
        edgeMaxPx: readNumberSetting(root.dataset.sheetEdgeMaxPx, DEFAULT_SHEET_MOTION.edgeMaxPx, 0),
        edgeDecay: readNumberSetting(root.dataset.sheetEdgeDecay, DEFAULT_SHEET_MOTION.edgeDecay, 1),
    };
}

